import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import { FaTimes, FaUser } from "react-icons/fa";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchRedesignMistakes,
  updateRedesignMistakeCategory,
  type MistakeEvent,
  type MistakePerson,
} from "../../store/admin/mistakeTrackerSlice";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import api from "../../utils/api";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminPagination,
  AdminSearchInput,
  AdminSelect,
  AdminTableCard,
  AdminTableState,
  adminTableCell,
  adminTableHead,
  formatAdminDateTime,
} from "../../components/admin/AdminPageShell";
import {
  PeriodPresetBar,
  rangeForPreset,
  type DatePreset,
} from "../../components/employee-dashboard/DashboardFilterControls";
import { CustomDatePicker } from "../../components/common/DatePickers";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import { isAdminLikeEmployee } from "../../utils/employeeRoles";
import ConfirmModal from "../../components/utilities/confirmModal";

const LIMIT = 25;

const REDESIGN_CATEGORIES = [
  "Query",
  "Design Preference",
  "Note Missed",
  "Design Mistake",
  "Files Mistake",
  "Client Preference Missed",
  "Need to Improve Design Quality",
  "Others (note required)",
];

const CategorySelect = ({
  categories,
  disabled,
  onChange,
}: {
  categories: string[];
  disabled: boolean;
  onChange: (category: string) => void;
}) => {
  const current = categories.length === 1 ? categories[0] : categories.join(", ");
  const options = [
    ...new Set([...REDESIGN_CATEGORIES, ...categories].filter(Boolean)),
  ];
  return (
    <select
      aria-label="Redesign category"
      className="max-w-[220px] rounded-md border border-[#E3E8E8] bg-white px-2 py-1 text-xs text-[#2C2C2C] disabled:opacity-60"
      value={current}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {!current ? <option value="">Category</option> : null}
      {categories.length > 1 ? <option value={current}>{current}</option> : null}
      {options.map((item) => (
        <option key={item} value={item}>
          {item}
        </option>
      ))}
    </select>
  );
};

const headerBtnClass =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition";
const headerBtnActiveClass =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-[#2fa9bf] text-xs font-medium";
type TrackerTab = "people" | "cases" | "types";
type KindFilter = "" | "internal" | "external";

const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: "", label: "All" },
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
];

const topCategories = (byCategory: Record<string, number>, limit = 3) =>
  Object.entries(byCategory)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit);

const kindBadgeClass = (kind: string) =>
  kind === "internal"
    ? "bg-violet-50 text-violet-800"
    : "bg-amber-50 text-amber-800";

const periodCopy = (
  preset: DatePreset,
  fromDate: string,
  toDate: string,
) => {
  if (preset === "day") return "today";
  if (preset === "week") return "this week";
  if (preset === "month") return "this month";
  return `${fromDate} to ${toDate}`;
};

const MistakeTracker = () => {
  const dispatch = useAppDispatch();
  const {
    summary,
    categoryOptions,
    peopleOptions,
    categories,
    people,
    events,
    page: activePage,
    totalPages,
    total,
    loading,
    error,
  } = useAppSelector((state) => state.mistakeTracker);
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { allEmployees } = useAppSelector((state) => state.employee);
  const empRole = (employee?.role || "").trim();
  const canAccessMistakes =
    user?.role === "Super Admin" ||
    isAdminLikeEmployee(empRole) ||
    empRole === "Super Admin";
  const canEditRedesignCategory = user?.role === "Super Admin";
  const [savingCategoryId, setSavingCategoryId] = useState("");
  const [pendingCategory, setPendingCategory] = useState<{
    event: MistakeEvent;
    category: string;
  } | null>(null);

  const monthRange = rangeForPreset("month");
  const [tab, setTab] = useState<TrackerTab>("people");
  const [preset, setPreset] = useState<DatePreset>("month");
  const [fromDate, setFromDate] = useState(monthRange.startDate);
  const [toDate, setToDate] = useState(monthRange.endDate);
  const [kind, setKind] = useState<KindFilter>("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [role, setRole] = useState("");
  const [empName, setEmpName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const resetPage = () => setPage(1);

  const handlePresetChange = (next: DatePreset) => {
    setPreset(next);
    resetPage();
    if (next === "custom") return;
    const range = rangeForPreset(next);
    setFromDate(range.startDate);
    setToDate(range.endDate);
  };

  useEffect(() => {
    if (!allEmployees?.length) dispatch(fetchAllEmployees());
  }, [allEmployees?.length, dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!canAccessMistakes) return;
    dispatch(
      fetchRedesignMistakes({
        fromDate,
        toDate,
        kind,
        category,
        severity,
        empName,
        role,
        search,
        page,
        limit: LIMIT,
      }),
    );
  }, [dispatch, canAccessMistakes, fromDate, toDate, kind, category, severity, empName, role, search, page]);

  const query = useMemo(
    () => ({
      fromDate,
      toDate,
      kind,
      category,
      severity,
      empName,
      role,
      search,
    }),
    [fromDate, toDate, kind, category, severity, empName, role, search],
  );

  const employeeNames = useMemo(() => {
    const seen = new Set<string>();
    const names: string[] = [];
    const add = (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      names.push(trimmed);
    };
    peopleOptions.forEach(add);
    (allEmployees || []).forEach((emp) => add(emp.emp_name));
    return names;
  }, [peopleOptions, allEmployees]);

  const selectedPerson = people.find(
    (person) => person.name.toLowerCase() === empName.toLowerCase(),
  );

  const kindLabel =
    kind === "internal"
      ? "internal redesigns"
      : kind === "external"
        ? "external redesigns"
        : "redesigns";

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/units/mistakes", {
        params: { ...query, page: 1, limit: 5000 },
      });
      const data = res.data.data as {
        people: typeof people;
        categories: typeof categories;
        events: typeof events;
      };
      const peopleRows = (data.people || []).map((person) => ({
        Employee: person.name,
        "Total cases": person.total,
        Designer: person.as_designer,
        QC2: person.as_qc2,
        CRM: person.as_crm,
        Minor: person.minor,
        Major: person.major,
        "New Design": person.new_design,
        Internal: person.internal,
        External: person.external,
        Categories: Object.entries(person.by_category)
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => `${name} (${count})`)
          .join("; "),
      }));
      const caseRows = (data.events || []).map((event) => ({
        Date: event.created_at,
        "Live case": event.live_case_id,
        Redesign: event.redesign_case_id,
        Source: event.source_case_id,
        Kind: event.kind === "internal" ? "Internal" : "External",
        Severity: event.severity || "",
        Categories: event.categories.join(", "),
        Designer: event.designer.join(", "),
        QC2: event.qc2,
        CRM: event.crm || "",
        Note: event.note,
      }));
      const typeRows = (data.categories || []).map((row) => ({
        Category: row.category,
        Cases: row.events,
        Internal: row.internal,
        External: row.external,
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(peopleRows),
        "People",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(typeRows),
        "Types",
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(caseRows),
        "Cases",
      );
      const who = empName ? `_${empName.replace(/\s+/g, "-")}` : "";
      const kindPart = kind || "all";
      XLSX.writeFile(
        workbook,
        `Mistakes_${kindPart}${who}_${fromDate}_to_${toDate}.xlsx`,
      );
    } finally {
      setExporting(false);
    }
  };

  const selectPerson = (name: string) => {
    setEmpName(name);
    setTab("cases");
    resetPage();
  };

  const changeRedesignCategory = (event: MistakeEvent, category: string) => {
    if (!canEditRedesignCategory || !category) return;
    if (event.categories.length === 1 && event.categories[0] === category) return;
    setPendingCategory({ event, category });
  };

  const confirmRedesignCategory = async () => {
    if (!pendingCategory) return;
    const { event, category } = pendingCategory;
    setSavingCategoryId(event.redesign_case_id);
    const result = await dispatch(
      updateRedesignMistakeCategory({
        kind: event.kind,
        redesign_case_id: event.redesign_case_id,
        categories: [category],
      }),
    );
    setSavingCategoryId("");
    if (updateRedesignMistakeCategory.fulfilled.match(result)) {
      setPendingCategory(null);
      dispatch(
        fetchRedesignMistakes({
          ...query,
          page,
          limit: LIMIT,
        }),
      );
      return;
    }
    window.alert(String(result.payload || "Failed to update redesign category"));
  };

  const showCrm = true;
  const showKindSplit = !kind;
  const showSeverity = true;
  const showScopeCols = kind === "internal" || kind === "external";
  const showNewDesignCol = kind === "external";
  const peopleColCount =
    5 +
    (showCrm ? 1 : 0) +
    (showKindSplit ? 2 : 0) +
    (showScopeCols ? 2 : 0) +
    (showNewDesignCol ? 1 : 0);
  const caseColCount =
    6 + (showKindSplit ? 1 : 0) + (showSeverity ? 1 : 0) + (showCrm ? 1 : 0);
  const typeColCount = kind ? 2 : 4;

  if (employee && !canAccessMistakes) {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Mistake Tracker</title>
      </Helmet>

      <AdminPageShell className="flex h-full min-h-0 flex-col gap-3 overflow-hidden text-[1.05rem]">
        <AdminPageHeader
          title="Mistake Tracker"
          subtitle="Filter internal or external redesigns by day, week, month, or employee (12 PM–next-day 12 PM IST, same as Daily Redesign). Categories are assigned to the source designer and captain/QC2. CRM is who sent the internal redesign or who uploaded the external case."
        >
          <div className="flex rounded-xl overflow-hidden border-2 border-white">
            {KIND_OPTIONS.map((option, index) => (
              <button
                key={option.value || "all"}
                type="button"
                onClick={() => {
                  setKind(option.value);
                  if (option.value === "internal" && severity === "New Design") {
                    setSeverity("");
                  }
                  resetPage();
                }}
                className={`${kind === option.value ? headerBtnActiveClass : headerBtnClass} rounded-none border-0 ${
                  index ? "border-l-2 border-white" : ""
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <PeriodPresetBar preset={preset} onChange={handlePresetChange} />
          <CustomDatePicker
            label="From"
            value={fromDate}
            max={toDate}
            variant="header"
            className="w-full sm:w-[150px]"
            onChange={(iso) => {
              setPreset("custom");
              setFromDate(iso);
              resetPage();
            }}
          />
          <CustomDatePicker
            label="To"
            value={toDate}
            min={fromDate}
            variant="header"
            className="w-full sm:w-[150px]"
            onChange={(iso) => {
              setPreset("custom");
              setToDate(iso);
              resetPage();
            }}
          />
          <EmployeeNameSelect
            names={employeeNames}
            value={empName}
            onChange={(name) => {
              setEmpName(name);
              if (name) setTab("cases");
              resetPage();
            }}
          />
          <AdminSelect
              value={severity}
              onChange={(value) => {
                setSeverity(value);
                resetPage();
              }}
            >
              <option value="">
                {kind === "internal"
                  ? "All Minor/Major"
                  : kind === "external"
                    ? "All Minor/Major/New Design"
                    : "All types"}
              </option>
              <option value="Minor">Minor</option>
              <option value="Major">Major</option>
              {kind !== "internal" ? (
                <option value="New Design">New Design</option>
              ) : null}
            </AdminSelect>
          <AdminSelect
            value={category}
            onChange={(value) => {
              setCategory(value);
              resetPage();
            }}
          >
            <option value="">All categories</option>
            {categoryOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </AdminSelect>
          <AdminSelect
            value={role}
            onChange={(value) => {
              setRole(value);
              resetPage();
            }}
          >
            <option value="">All roles</option>
            <option value="designer">Designer</option>
            <option value="qc2">Captain / QC2</option>
            {showCrm ? <option value="crm">CRM</option> : null}
          </AdminSelect>
          <AdminSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search case or note…"
          />
          <button
            type="button"
            onClick={exportExcel}
            disabled={exporting}
            className={headerBtnClass}
          >
            {exporting ? "Exporting…" : "Export Excel"}
          </button>
        </AdminPageHeader>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex rounded-xl overflow-hidden border border-[#DDDDD8] bg-white">
            {(["people", "cases", "types"] as TrackerTab[]).map((id, index) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`px-4 py-2 text-xs font-medium ${
                  tab === id
                    ? "bg-[#2fa9bf] text-white"
                    : "bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
                } ${index ? "border-l border-[#DDDDD8]" : ""}`}
              >
                {id === "people" ? "People" : id === "cases" ? "Cases" : "Types"}
              </button>
            ))}
          </div>
          {tab === "cases" ? (
            <AdminPagination
              page={activePage}
              totalPages={totalPages}
              onPrev={() => setPage((p) => p - 1)}
              onNext={() => setPage((p) => p + 1)}
              disabled={loading}
            />
          ) : null}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: kind
                ? `${KIND_OPTIONS.find((o) => o.value === kind)?.label} redesigns`
                : "Redesigns",
              value: summary.events,
            },
            ...(kind
              ? [
                  { label: "Minor", value: summary.minor },
                  { label: "Major", value: summary.major },
                  ...(kind === "external"
                    ? [{ label: "New Design", value: summary.new_design }]
                    : []),
                ]
              : [
                  { label: "Internal", value: summary.internal },
                  { label: "External", value: summary.external },
                ]),
            { label: "People", value: summary.people },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-[#E8E8E4] bg-white px-4 py-3 shadow-sm"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                {card.label}
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[#4A4A48]">
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {empName ? (
          <EmployeeReport
            name={empName}
            person={selectedPerson}
            kindLabel={kindLabel}
            period={periodCopy(preset, fromDate, toDate)}
            category={category}
            onClear={() => {
              setEmpName("");
              resetPage();
            }}
            onPickCategory={(value) => {
              setCategory(value);
              setTab("cases");
              resetPage();
            }}
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {tab === "people" ? (
          <AdminTableCard className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-[#F8F8F6]">
                <tr>
                  <th className={adminTableHead}>Employee</th>
                  <th className={`${adminTableHead} text-right`}>Cases</th>
                  <th className={`${adminTableHead} text-right`}>Designer</th>
                  <th className={`${adminTableHead} text-right`}>Captain/QC2</th>
                  {showCrm ? (
                    <th className={`${adminTableHead} text-right`}>CRM</th>
                  ) : null}
                  {showKindSplit ? (
                    <>
                      <th className={`${adminTableHead} text-right`}>Internal</th>
                      <th className={`${adminTableHead} text-right`}>External</th>
                    </>
                  ) : null}
                  {showScopeCols ? (
                    <>
                      <th className={`${adminTableHead} text-right`}>Minor</th>
                      <th className={`${adminTableHead} text-right`}>Major</th>
                    </>
                  ) : null}
                  {showNewDesignCol ? (
                    <th className={`${adminTableHead} text-right`}>New Design</th>
                  ) : null}
                  <th className={adminTableHead}>Categories</th>
                </tr>
              </thead>
              <tbody>
                {loading && !people.length ? (
                  <AdminTableState
                    colSpan={peopleColCount}
                    variant="loading"
                    message="Loading mistakes…"
                  />
                ) : !people.length ? (
                  <AdminTableState
                    colSpan={peopleColCount}
                    variant="empty"
                    message={`No ${kindLabel} in this range.`}
                  />
                ) : (
                  people.map((person) => (
                    <PersonRow
                      key={person.name}
                      person={person}
                      active={empName.toLowerCase() === person.name.toLowerCase()}
                      showCrm={showCrm}
                      showKindSplit={showKindSplit}
                      showSeverity={showScopeCols}
                      showNewDesign={showNewDesignCol}
                      onSelect={() => selectPerson(person.name)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </AdminTableCard>
        ) : null}

        {tab === "cases" ? (
          <AdminTableCard className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-[#F8F8F6]">
                <tr>
                  <th className={adminTableHead}>When</th>
                  <th className={adminTableHead}>Case</th>
                  {showKindSplit ? <th className={adminTableHead}>Kind</th> : null}
                  {showSeverity ? <th className={adminTableHead}>Minor/Major</th> : null}
                  <th className={adminTableHead}>Categories</th>
                  <th className={adminTableHead}>Designer</th>
                  <th className={adminTableHead}>Captain/QC2</th>
                  {showCrm ? <th className={adminTableHead}>CRM</th> : null}
                  <th className={adminTableHead}>Note</th>
                </tr>
              </thead>
              <tbody>
                {loading && !events.length ? (
                  <AdminTableState
                    colSpan={caseColCount}
                    variant="loading"
                    message="Loading cases…"
                  />
                ) : !events.length ? (
                  <AdminTableState
                    colSpan={caseColCount}
                    variant="empty"
                    message={
                      empName
                        ? `${empName} has no ${kindLabel} ${periodCopy(preset, fromDate, toDate)}.`
                        : `No ${kindLabel} in this range.`
                    }
                  />
                ) : (
                  events.map((event) => (
                    <tr
                      key={`${event.redesign_case_id}-${event.created_at}`}
                      className="border-t border-[#F0F0EC]"
                    >
                      <td className={`${adminTableCell} whitespace-nowrap`}>
                        {formatAdminDateTime(event.created_at)}
                      </td>
                      <td className={adminTableCell}>
                        <Link
                          to={caseHrefFromAllocationId(event.redesign_case_id)}
                          className="font-medium text-[#2fa9bf] hover:underline"
                        >
                          {event.redesign_case_id}
                        </Link>
                        <div className="text-[11px] text-[#8A8A85]">
                          Live {event.live_case_id}
                        </div>
                      </td>
                      {showKindSplit ? (
                        <td className={adminTableCell}>
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${kindBadgeClass(
                              event.kind,
                            )}`}
                          >
                            {event.kind === "internal" ? "Internal" : "External"}
                          </span>
                        </td>
                      ) : null}
                      {showSeverity ? (
                        <td className={adminTableCell}>
                          {event.severity ? (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                event.severity === "Major"
                                  ? "bg-rose-50 text-rose-800"
                                  : event.severity === "New Design"
                                    ? "bg-emerald-50 text-emerald-800"
                                    : "bg-sky-50 text-sky-800"
                              }`}
                            >
                              {event.severity}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      ) : null}
                      <td className={adminTableCell}>
                        {canEditRedesignCategory ? (
                          <CategorySelect
                            categories={event.categories}
                            disabled={savingCategoryId === event.redesign_case_id}
                            onChange={(category) =>
                              changeRedesignCategory(event, category)
                            }
                          />
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {event.categories.map((item) => (
                              <span
                                key={item}
                                className="inline-flex rounded-full bg-[#E8F6F8] px-2 py-0.5 text-[11px] text-[#1A6B78]"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={adminTableCell}>
                        {event.designer.join(", ") || "—"}
                      </td>
                      <td className={adminTableCell}>{event.qc2 || "—"}</td>
                      {showCrm ? (
                        <td className={adminTableCell}>
                          {event.crm || "—"}
                        </td>
                      ) : null}
                      <td className={`${adminTableCell} max-w-[220px]`}>
                        <span className="line-clamp-2 text-[#6B6B66]">
                          {event.note || "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {total ? (
              <p className="px-5 py-2 text-xs text-[#8A8A85]">
                {total} case{total === 1 ? "" : "s"}
                {query.empName ? ` for ${query.empName}` : ""}
                {kind ? ` · ${kindLabel}` : ""}
              </p>
            ) : null}
          </AdminTableCard>
        ) : null}

        {tab === "types" ? (
          <AdminTableCard className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 bg-[#F8F8F6]">
                <tr>
                  <th className={adminTableHead}>Category</th>
                  <th className={`${adminTableHead} text-right`}>Cases</th>
                  {showKindSplit ? (
                    <>
                      <th className={`${adminTableHead} text-right`}>Internal</th>
                      <th className={`${adminTableHead} text-right`}>External</th>
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {loading && !categories.length ? (
                  <AdminTableState
                    colSpan={typeColCount}
                    variant="loading"
                    message="Loading types…"
                  />
                ) : !categories.length ? (
                  <AdminTableState
                    colSpan={typeColCount}
                    variant="empty"
                    message="No categories in this range."
                  />
                ) : (
                  categories.map((row) => (
                    <tr
                      key={row.category}
                      className="border-t border-[#F0F0EC] cursor-pointer hover:bg-[#F8F8F6]"
                      onClick={() => {
                        setCategory(row.category);
                        setTab("cases");
                        resetPage();
                      }}
                    >
                      <td className={`${adminTableCell} font-medium`}>
                        {row.category}
                      </td>
                      <td className={`${adminTableCell} text-right tabular-nums`}>
                        {row.events}
                      </td>
                      {showKindSplit ? (
                        <>
                          <td className={`${adminTableCell} text-right tabular-nums`}>
                            {row.internal}
                          </td>
                          <td className={`${adminTableCell} text-right tabular-nums`}>
                            {row.external}
                          </td>
                        </>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </AdminTableCard>
        ) : null}
      </AdminPageShell>
      <ConfirmModal
        isOpen={!!pendingCategory}
        loading={!!savingCategoryId}
        onClose={() => {
          if (!savingCategoryId) setPendingCategory(null);
        }}
        onConfirm={confirmRedesignCategory}
        title="Change category"
        message={
          pendingCategory ? (
            <>
              Change{" "}
              <strong>{pendingCategory.event.redesign_case_id}</strong> from{" "}
              <strong>
                {pendingCategory.event.categories.join(", ") || "—"}
              </strong>{" "}
              to <strong>{pendingCategory.category}</strong>?
            </>
          ) : null
        }
        confirmLabel="Yes, change"
        cancelLabel="Cancel"
      />
    </>
  );
};

const EmployeeNameSelect = ({
  names,
  value,
  onChange,
}: {
  names: string[];
  value: string;
  onChange: (name: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    const onDoc = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return names.slice(0, 80);
    return names.filter((name) => name.toLowerCase().includes(q)).slice(0, 80);
  }, [names, query]);

  return (
    <div ref={wrapRef} className="relative w-full sm:w-[240px]">
      <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs pointer-events-none" />
      <input
        type="search"
        value={open ? query : value}
        placeholder="Filter by employee…"
        className="w-full rounded-xl bg-white border border-white text-[#1A1A18] placeholder:text-[#8A8A85] outline-none shadow-sm focus:ring-2 focus:ring-white/70 pl-8 pr-8 py-2.5 text-xs"
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered[0]) {
            onChange(filtered[0]);
            setOpen(false);
          }
          if (e.key === "Escape") setOpen(false);
        }}
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear employee"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#4A4A48]"
          onClick={() => {
            onChange("");
            setQuery("");
            setOpen(false);
          }}
        >
          <FaTimes className="text-xs" />
        </button>
      ) : null}
      {open ? (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] max-h-72 overflow-y-auto rounded-xl border border-[#E8E8E4] bg-white shadow-lg">
          {filtered.length ? (
            filtered.map((name) => (
              <button
                key={name}
                type="button"
                className={`block w-full px-3 py-2 text-left text-sm ${
                  value.toLowerCase() === name.toLowerCase()
                    ? "bg-[#E8F6F8] text-[#1A6B78]"
                    : "text-[#4A4A48] hover:bg-[#F8F8F6]"
                }`}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
              >
                {name}
              </button>
            ))
          ) : (
            <p className="px-3 py-4 text-sm text-[#8A8A85]">No employee matches</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

const EmployeeReport = ({
  name,
  person,
  kindLabel,
  period,
  category,
  onClear,
  onPickCategory,
}: {
  name: string;
  person?: MistakePerson;
  kindLabel: string;
  period: string;
  category: string;
  onClear: () => void;
  onPickCategory: (category: string) => void;
}) => {
  const cats = Object.entries(person?.by_category || {}).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );
  return (
    <div className="rounded-2xl border border-[#E8E8E4] bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-[#4A4A48]">
            <strong>{name}</strong> has{" "}
            <strong>{person?.total || 0}</strong> {kindLabel} {period}
          </p>
          <p className="mt-1 text-xs text-[#8A8A85]">
            Designer {person?.as_designer || 0}
            {" · "}
            Captain/QC2 {person?.as_qc2 || 0}
            {person?.as_crm ? ` · CRM ${person.as_crm}` : ""}
            {person && (person.minor || person.major)
              ? ` · Minor ${person.minor || 0} · Major ${person.major || 0}`
              : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs font-medium text-[#2fa9bf] hover:underline"
        >
          Clear employee
        </button>
      </div>
      {cats.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {cats.map(([label, count]) => (
            <button
              key={label}
              type="button"
              onClick={() => onPickCategory(category === label ? "" : label)}
              className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ${
                category === label
                  ? "bg-[#2fa9bf] text-white"
                  : "bg-[#E8F6F8] text-[#1A6B78] hover:bg-[#d5eef2]"
              }`}
            >
              {label} {count}
            </button>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs text-[#8A8A85]">
          No categories for this employee in the selected filters.
        </p>
      )}
    </div>
  );
};

const PersonRow = ({
  person,
  active,
  showCrm,
  showKindSplit,
  showSeverity,
  showNewDesign,
  onSelect,
}: {
  person: MistakePerson;
  active: boolean;
  showCrm: boolean;
  showKindSplit: boolean;
  showSeverity: boolean;
  showNewDesign: boolean;
  onSelect: () => void;
}) => (
  <tr
    className={`border-t border-[#F0F0EC] cursor-pointer ${
      active ? "bg-[#E8F6F8]" : "hover:bg-[#F8F8F6]"
    }`}
    onClick={onSelect}
  >
    <td className={`${adminTableCell} font-medium`}>{person.name}</td>
    <td className={`${adminTableCell} text-right tabular-nums font-semibold`}>
      {person.total}
    </td>
    <td className={`${adminTableCell} text-right tabular-nums`}>
      {person.as_designer || "—"}
    </td>
    <td className={`${adminTableCell} text-right tabular-nums`}>
      {person.as_qc2 || "—"}
    </td>
    {showCrm ? (
      <td className={`${adminTableCell} text-right tabular-nums`}>
        {person.as_crm || "—"}
      </td>
    ) : null}
    {showKindSplit ? (
      <>
        <td className={`${adminTableCell} text-right tabular-nums`}>
          {person.internal || "—"}
        </td>
        <td className={`${adminTableCell} text-right tabular-nums`}>
          {person.external || "—"}
        </td>
      </>
    ) : null}
    {showSeverity ? (
      <>
        <td className={`${adminTableCell} text-right tabular-nums`}>
          {person.minor || "—"}
        </td>
        <td className={`${adminTableCell} text-right tabular-nums`}>
          {person.major || "—"}
        </td>
      </>
    ) : null}
    {showNewDesign ? (
      <td className={`${adminTableCell} text-right tabular-nums`}>
        {person.new_design || "—"}
      </td>
    ) : null}
    <td className={adminTableCell}>
      <div className="flex flex-wrap gap-1">
        {topCategories(person.by_category, 4).map(([name, count]) => (
          <span
            key={name}
            className="inline-flex rounded-full bg-[#E8F6F8] px-2 py-0.5 text-[11px] text-[#1A6B78]"
          >
            {name} {count}
          </span>
        ))}
      </div>
    </td>
  </tr>
);

export default MistakeTracker;
